import type { EvidenceSpan } from '@finder/contracts';

/** Human label for an evidence span. Text, never a color-only cue. */
export function evidenceKindLabel(kind: EvidenceSpan['kind']): string {
  switch (kind) {
    case 'food_drink':
      return 'Free food or drink';
    case 'free_attendance':
      return 'Free to attend';
    case 'public_access':
      return 'Open to the public';
    case 'restriction':
      return 'Who can attend';
    case 'status':
      return 'Status';
  }
}

/**
 * Display form of a source description. Tags from feeds (UW HTML) are stripped
 * so the detail view stays readable, and zero-width characters are dropped.
 * React still escapes the result — this is presentation, not a safety boundary.
 */
export function plainDescription(description: string): string {
  return description
    .replace(/<[^>]+>/g, '')
    .replace(/[\u200b\u200c\u200d\ufeff]/g, '')
    .replace(/\n{3,}/g, '\n\n')
    .trim();
}

export interface TextRun {
  text: string;
  mark: boolean;
}

/**
 * Split `description` into runs, marking exact evidence quotes so the phrase
 * that qualified the event is visible in context. Longer quotes win when they
 * overlap. Quotes that are not in the description are ignored here; the
 * evidence list still shows them.
 */
export function splitDescriptionByQuotes(
  description: string,
  quotes: readonly string[],
): TextRun[] {
  if (!description) return [];
  const lower = description.toLowerCase();
  const ranges: Array<{ start: number; end: number }> = [];
  const sorted = [...quotes].filter((q) => q.length > 0).sort((a, b) => b.length - a.length);
  for (const quote of sorted) {
    const needle = quote.toLowerCase();
    const idx = lower.indexOf(needle);
    if (idx === -1) continue;
    const end = idx + needle.length;
    if (ranges.some((r) => idx < r.end && end > r.start)) continue;
    ranges.push({ start: idx, end });
  }
  ranges.sort((a, b) => a.start - b.start);
  const runs: TextRun[] = [];
  let cursor = 0;
  for (const range of ranges) {
    if (range.start > cursor) {
      runs.push({ text: description.slice(cursor, range.start), mark: false });
    }
    runs.push({ text: description.slice(range.start, range.end), mark: true });
    cursor = range.end;
  }
  if (cursor < description.length) runs.push({ text: description.slice(cursor), mark: false });
  return runs;
}
